import type { NarrativeTime } from "@/data/schema/scene";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const FULL_MONTHS = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
] as const;

interface ParsedStart {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Clock label for the scene caption. Falls back to the prose label. */
export function formatStoryTime(time: NarrativeTime | undefined): string | null {
  if (time === undefined) {
    return null;
  }
  // An unknown time can still have a guess to sort by. The guess is not worth showing.
  const parsed = time.mode === "unknown" ? null : parseApproxStart(time.approxStart);
  if (parsed === null) {
    return time.label;
  }
  const rough = roughDate(parsed, time.precision);
  if (rough !== null) {
    return rough;
  }
  return `${formatDate(parsed)}, ${formatClock(parsed, time.confidence)}`;
}

/** Calendar date with no clock. Falls back to the prose label. */
export function formatStoryDate(time: NarrativeTime | undefined): string | null {
  if (time === undefined) {
    return null;
  }
  const parsed = time.mode === "unknown" ? null : parseApproxStart(time.approxStart);
  if (parsed === null) {
    return time.label;
  }
  return roughDate(parsed, time.precision)
    ?? `${FULL_MONTHS[parsed.month - 1]} ${parsed.day}, ${parsed.year}`;
}

/** Only the month or year, when that is all the book says. The rest of approxStart is a guess. */
function roughDate(parts: ParsedStart, precision: NarrativeTime["precision"]): string | null {
  if (precision === "year") {
    return `${parts.year}`;
  }
  if (precision === "month") {
    return `${FULL_MONTHS[parts.month - 1]} ${parts.year}`;
  }
  return null;
}

function parseApproxStart(value: string | undefined): ParsedStart | null {
  if (value === undefined) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (match === null) {
    return null;
  }
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

function formatDate(parts: ParsedStart): string {
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const weekday = WEEKDAYS[utc.getUTCDay()];
  const month = MONTHS[parts.month - 1];
  return `${weekday} ${parts.day} ${month} ${parts.year}`;
}

function formatClock(
  parts: ParsedStart,
  confidence: NarrativeTime["confidence"],
): string {
  if (confidence === "low") {
    return hourLabel(parts.hour);
  }
  return clockLabel(parts.hour, parts.minute);
}

function hourLabel(hour: number): string {
  return `${twelveHour(hour)} ${meridiem(hour)}`;
}

function clockLabel(hour: number, minute: number): string {
  if (minute === 0) {
    return hourLabel(hour);
  }
  const minutes = minute.toString().padStart(2, "0");
  return `${twelveHour(hour)}:${minutes} ${meridiem(hour)}`;
}

function twelveHour(hour: number): number {
  const wrapped = hour % 12;
  return wrapped === 0 ? 12 : wrapped;
}

function meridiem(hour: number): "AM" | "PM" {
  return hour >= 12 ? "PM" : "AM";
}
